import lodash from 'lodash';
import { FC, useEffect, useMemo } from 'react';
import { $Formutil, connect } from 'react-bootstrap-formutil';
import Skeleton from 'components/base/Skeleton';
import ErrorBox from 'components/ErrorBox';
import WeakNetPrompt from 'components/forms/WeakNetPrompt';
import FormSelect, { OptionItem } from 'components/FormSelect';
import { useGetCountryList } from 'hooks/useGetCountryList';
import { CountryItem, CountryTypeEnum } from 'types/common';
import API from 'utils/API';
import CountryCode from './CountryCode';

export interface BaseCountrySelectProps {
    isSSN: boolean;
    disabled?: boolean;
    // 指定配置化接口请求field类型
    type?: CountryTypeEnum;
    // 仅限指定的国家列表，用于自定义国家选项
    limitCountryList?: Array<CountryItem['country_code']>;
    // 额外的国家选项，用于在开户国家列表之外的情况。比如下掉了某些国家，但仍然需要在列表中展示。
    extraCountryItems?: CountryItem[];
    // ip定位可选参数
    needLocation?: boolean;
    // 字段名，定位时会使用该字段名进行预填充
    fieldName?: string;
    // 如果只有limitCountryList指定的少数几个选项，则使用select模式。例如签发国一般为国籍和居住国二选一。
    mode?: 'select' | 'search';
    placeholder?: string;
}

interface CountrySelectProps extends BaseCountrySelectProps {
    $formutil: $Formutil;
}

/**
 * SSN与非SSN开户国家选择器的基础公共组件，支持传入参数开启ip定位国家预填
 */
const CountrySelect: FC<CountrySelectProps> = ({
    mode,
    placeholder = __('请选择国家或者地区'),
    type,
    limitCountryList,
    needLocation,
    fieldName,
    isSSN,
    $formutil,
    extraCountryItems,
    ...props
}) => {
    const finalMode = mode || (limitCountryList && limitCountryList.length > 0 ? 'select' : 'search');
    const isSelectMode = finalMode === 'select';
    const { $params, $setValues } = $formutil;
    const fieldValue = fieldName ? $params[fieldName] : '';
    const url = API.INFOBASE.getCountryList(type || (isSSN ? CountryTypeEnum.SSN : CountryTypeEnum.NON_SSN));

    const { data, isLoading, mutate, error } = useGetCountryList(url, needLocation, extraCountryItems);
    const { countryData: originalCountryData = [], locationCountry } = data || {};
    const countryItemList = useMemo<CountryItem[]>(
        () => lodash.flatMap(originalCountryData, 'items'),
        [originalCountryData]
    );

    useEffect(() => {
        if (countryItemList.length > 0 && needLocation && locationCountry && fieldName) {
            // 判断当前国家列表中是否包含定位的国家
            const isLocationInCountryList: boolean = countryItemList.some(
                item => item.country_code === locationCountry
            );

            // 仅在表单字段无值时进行预填充
            if (isLocationInCountryList && !fieldValue) {
                $setValues({
                    [fieldName]: locationCountry
                });
            }
        }
    }, [countryItemList, locationCountry, needLocation, fieldValue, fieldName, $setValues]);

    if (error) {
        return (
            <div className="mb-3">
                <ErrorBox error={error} onClick={() => mutate()} />
                {/* loading/error状态下不能直接提交,需要拦截一下 */}
                <WeakNetPrompt name={`temporary_country_select_${fieldName || ''}`} type="error" />
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="mb-3">
                <Skeleton line={2} />
                {/* loading状态下不能直接提交,需要拦截一下 */}
                <WeakNetPrompt name={`temporary_country_select_${fieldName || ''}`} type="loading" />
            </div>
        );
    }

    const countryOptions = isSelectMode
        ? countryItemList.reduce((prev, item: CountryItem) => {
              if (limitCountryList && !limitCountryList.includes(item.country_code)) {
                  return prev;
              }

              return prev.concat({
                  label: item.country,
                  value: item.country_code
              });
          }, [] as OptionItem[])
        : [];

    return isSelectMode ? (
        <FormSelect options={countryOptions} placeholder={placeholder} {...props} />
    ) : (
        <CountryCode countryData={originalCountryData} placeholder={placeholder} {...props} />
    );
};

export default connect(CountrySelect);
